import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useIsMobile } from '@ori/presentation-hooks';
import useTestId from '@ori/testid-generator';
import { useKeenSlider } from 'keen-slider/react';
import debounce from 'lodash.debounce';
import get from 'lodash.get';
import React, { useCallback, useEffect, Children, useState, useMemo } from 'react';
import { v4 } from 'uuid';
import { SLIDER_CLASSNAME, SLIDE_CLASSNAME, WAIT_TIME } from "./constants";
import { useAutoplayProps, useNavigationProps, usePaginationProps, useSliderProps } from "./hooks";
import { NavigationArrows } from "./NavigationArrows";
import { Pagination } from "./Pagination";
import { usePlugins } from "./plugins";
import { CarouselInner, CarouselRoot } from "./styles";
import { disableDragEventPropagation, isCarouselAtTheEnd, removeDisableDragEventPropagation, shouldShowNavigationArrows } from "./utils";
import { CAROUSEL, CAROUSEL_WRAPPER } from "../../common";
import { ImagePlaceholder } from "../ImagePlaceholder";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var Carousel = function Carousel(_ref) {
  var autoplay = _ref.autoplay,
    className = _ref.className,
    children = _ref.children,
    navigation = _ref.navigation,
    pagination = _ref.pagination,
    propagateDragEvent = _ref.propagateDragEvent,
    testId = _ref.testId,
    slider = _ref.slider,
    width = _ref.width;
  var autoplayProps = useAutoplayProps(autoplay);
  var navigationProps = useNavigationProps(navigation);
  var paginationProps = usePaginationProps(pagination);
  var selectorId = useMemo(function () {
    return "x".concat(v4());
  }, []);
  var sliderProps = useSliderProps(_objectSpread({
    selector: "#".concat(selectorId, " > .").concat(SLIDE_CLASSNAME)
  }, slider));
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    currentSlide = _useState2[0],
    setCurrentSlide = _useState2[1];
  var _useState3 = useState(Children.count(children)),
    _useState4 = _slicedToArray(_useState3, 2),
    slidesCount = _useState4[0],
    setSlidesCount = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    lastSlideVisible = _useState6[0],
    setLastSlideVisible = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isTouchActive = _useState8[0],
    setIsTouchActive = _useState8[1];
  var isMobile = useIsMobile();
  var _useTestId = useTestId(),
    getTestId = _useTestId.getTestId;
  var updateLastSlideVisible = useCallback(function (progress) {
    setLastSlideVisible(isCarouselAtTheEnd(progress));
  }, []);
  var options = useMemo(function () {
    var updateLastSlideVisibleDebounce = debounce(updateLastSlideVisible, WAIT_TIME);
    return _objectSpread(_objectSpread({}, sliderProps), {}, {
      created: function created(instance) {
        var _sliderProps$created;
        setSlidesCount(get(instance, 'track.details.slides.length', Children.count(children)));
        disableDragEventPropagation({
          instance: instance,
          propagateDragEvent: propagateDragEvent
        });
        (_sliderProps$created = sliderProps.created) === null || _sliderProps$created === void 0 || _sliderProps$created.call(sliderProps, instance);
      },
      destroyed: function destroyed(instance) {
        var _sliderProps$destroye;
        removeDisableDragEventPropagation({
          instance: instance,
          propagateDragEvent: propagateDragEvent
        });
        (_sliderProps$destroye = sliderProps.destroyed) === null || _sliderProps$destroye === void 0 || _sliderProps$destroye.call(sliderProps, instance);
      },
      slideChanged: function slideChanged(instance) {
        var _sliderProps$slideCha;
        setCurrentSlide(get(instance, 'track.details.rel', 0));
        (_sliderProps$slideCha = sliderProps.slideChanged) === null || _sliderProps$slideCha === void 0 || _sliderProps$slideCha.call(sliderProps, instance);
      },
      detailsChanged: function detailsChanged(instance) {
        var _sliderProps$detailsC;
        updateLastSlideVisibleDebounce(get(instance, 'details.progress', 0));
        (_sliderProps$detailsC = sliderProps.detailsChanged) === null || _sliderProps$detailsC === void 0 || _sliderProps$detailsC.call(sliderProps, instance);
      }
    });
  }, [children, propagateDragEvent, sliderProps, updateLastSlideVisible]);
  var _useKeenSlider = useKeenSlider(options, usePlugins(useMemo(function () {
      return {
        autoplay: autoplayProps,
        children: children,
        className: className,
        navigation: navigationProps,
        pagination: paginationProps,
        propagateDragEvent: propagateDragEvent,
        slider: sliderProps,
        testId: testId,
        width: width
      };
    }, [autoplayProps, children, className, navigationProps, paginationProps, propagateDragEvent, sliderProps, testId, width]), setIsTouchActive)),
    _useKeenSlider2 = _slicedToArray(_useKeenSlider, 2),
    sliderRef = _useKeenSlider2[0],
    instanceRef = _useKeenSlider2[1];
  useEffect(function () {
    var childrenCount = Children.count(children);
    if (instanceRef.current) {
      instanceRef.current.update(options, 0);
      setSlidesCount(get(instanceRef, 'current.track.details.slides.length', childrenCount));
    } else {
      setSlidesCount(childrenCount);
    }
  }, [children, instanceRef, options]);
  if (slidesCount <= 1) {
    return /*#__PURE__*/_jsx(CarouselRoot, {
      "data-testid": getTestId(CAROUSEL_WRAPPER),
      width: width,
      children: slidesCount === 0 ? /*#__PURE__*/_jsx(ImagePlaceholder, {
        isSquare: true
      }) : children
    });
  }
  return /*#__PURE__*/_jsxs(CarouselRoot, {
    className: className,
    "data-testid": testId !== null && testId !== void 0 ? testId : getTestId(CAROUSEL_WRAPPER),
    isTouchActive: isTouchActive,
    navigation: navigationProps,
    width: width,
    children: [/*#__PURE__*/_jsx(CarouselInner, {
      ref: sliderRef,
      id: selectorId,
      className: SLIDER_CLASSNAME,
      "data-testid": getTestId(CAROUSEL),
      children: children
    }), paginationProps.show ? /*#__PURE__*/_jsx(Pagination, {
      autoplay: autoplayProps,
      slidesCount: slidesCount,
      currentSlide: currentSlide,
      isMobile: isMobile,
      instanceRef: instanceRef,
      pagination: paginationProps
    }) : null, shouldShowNavigationArrows({
      navigation: navigationProps,
      slider: sliderProps,
      slidesCount: slidesCount
    }) ? /*#__PURE__*/_jsx(NavigationArrows, {
      instanceRef: instanceRef,
      lastSlideVisible: lastSlideVisible,
      navigation: navigationProps,
      slider: sliderProps,
      slidesCount: slidesCount
    }) : null]
  });
};
//# sourceMappingURL=Carousel.js.map